/**
 * The channel registry — the ONE place a channel's identity lives.
 *
 * Every page renders channel names, agent-step labels and capability states
 * from here, never from string literals. Adding a channel to AdPilot is a row
 * here plus its backend provider support; the pages pick it up through
 * ChannelTabs without further edits. (The Brief page used to hardcode
 * `meta.read_insights` / "It reads Meta" everywhere — those literals are what
 * this registry replaces.)
 *
 * `key` doubles as the backend `provider` value on adpilot_connections, so a
 * tab's identity IS the API parameter.
 */
export const CHANNELS = {

  facebook: {
    key: 'facebook',
    label: 'Meta',
    accountNoun: 'ad account',
    agentTitle: 'Campaign optimization agent',
    agentSubtitle: 'Meta Ads · Campaign operator',
    // The agent-step rail: read → plan → apply. Tool-call ids are display
    // identity, styled as code — per channel so the rail never lies about
    // which platform a step touches.
    tools: {
      read: 'meta.read_insights',
      plan: 'optimizer.plan_actions',
      apply: 'meta.update_budget',
    },
    copy: {
      briefSubtitle: 'Give AdPilot a run. It reads Meta, reasons about pacing, and brings every proposed action back for approval.',
      readReady: 'Ready to read Meta',
      connectFirst: 'Connect Meta before approving changes.',
      appliedIn: 'Meta',
      emptyPitch: 'I’ll read your Meta performance and bring you clear campaign recommendations. I can’t publish anything until you approve it.',
      connectCta: 'Connect Meta',
      activitySubtitle: 'A clear record of what AdPilot analyzed, what you approved, and what changed in Meta.',
      createTitle: 'Turn an idea into a Meta campaign.',
    },
    capabilities: { create: true, monitor: true, optimize: true, apply: true },
  },
  'google-ads': {
    key: 'google-ads',
    label: 'Google Ads',
    accountNoun: 'Google Ads account',
    agentTitle: 'Campaign optimization agent',
    agentSubtitle: 'Google Ads · Campaign optimizer',
    tools: {
      read: 'googleads.read_performance',
      plan: 'optimizer.plan_actions',
      apply: 'googleads.apply_edit',
    },
    copy: {
      briefSubtitle: 'Give AdPilot a run. It reads Google Ads, reasons about the whole campaign — budgets, keywords, negatives, status, geography — and brings every proposed action back for approval.',
      readReady: 'Ready to read Google Ads',
      connectFirst: 'Connect Google Ads before approving changes.',
      appliedIn: 'Google Ads',
      emptyPitch: 'I’ll read your Google Ads performance and bring you clear, reviewable changes — budgets, keywords, negatives and more. I can’t publish anything until you approve it.',
      connectCta: 'Connect Google Ads',
      activitySubtitle: 'A clear record of what AdPilot analyzed, what you approved, and what changed in Google Ads.',
      createTitle: 'Turn an idea into a Google Search campaign.',
    },
    capabilities: { create: true, monitor: true, optimize: true, apply: true },
  },
  'chatgpt-ads': {
    key: 'chatgpt-ads',
    label: 'ChatGPT Ads',
    accountNoun: 'ChatGPT Ads account',
    agentTitle: 'Campaign optimization agent',
    agentSubtitle: 'ChatGPT Ads · Campaign optimizer',
    tools: {
      read: 'chatgptads.read_performance',
      plan: 'optimizer.plan_actions',
      apply: 'chatgptads.apply_edit',
    },
    copy: {
      briefSubtitle: 'Give AdPilot a run. It reads ChatGPT Ads performance and brings every proposed action back for approval.',
      readReady: 'Ready to read ChatGPT Ads',
      connectFirst: 'Connect ChatGPT Ads before approving changes.',
      appliedIn: 'ChatGPT Ads',
      emptyPitch: 'I’ll read your ChatGPT Ads performance and bring you clear, reviewable changes. I can’t publish anything until you approve it.',
      connectCta: 'Connect ChatGPT Ads',
      activitySubtitle: 'A clear record of what AdPilot analyzed, what you approved, and what changed in ChatGPT Ads.',
      createTitle: 'Campaign creation for ChatGPT Ads is coming.',
    },
    capabilities: { create: false, monitor: true, optimize: true, apply: true },
  },
  'universal-ads': {
    key: 'universal-ads', label: 'Universal Ads', accountNoun: 'Universal Ads account',
    agentTitle: 'Streaming TV campaign agent', agentSubtitle: 'Universal Ads · Campaign management',
    tools: { read: 'universalads.report', plan: 'universalads.save_draft', apply: 'universalads.publish' },
    copy: { briefSubtitle: 'Review streaming TV delivery and video performance.', readReady: 'Ready to read Universal Ads', connectFirst: 'Connect Universal Ads first.', appliedIn: 'Universal Ads', emptyPitch: 'Connect your streaming TV account to review performance and manage campaigns.', connectCta: 'Connect Universal Ads', activitySubtitle: 'Your Universal Ads activity and approved changes.', createTitle: 'Turn an idea into a Universal Ads campaign.' },
    capabilities: { create: true, monitor: true, optimize: true, apply: true },
  },
};

// Google Ads graduated from its rollout flag (#2732 reverted): BOTH channels
// are always in the registry — connect tab, admin list, channel-aware pages.
export const CHANNEL_LIST = [CHANNELS.facebook, CHANNELS['google-ads'], CHANNELS['chatgpt-ads'], CHANNELS['universal-ads']];

export const channelFor = (provider) => CHANNELS[provider] || CHANNELS.facebook;

/**
 * The tool-call label a recommendation card shows for one action type —
 * `googleads.update_keywords`, `meta.update_budget`, … Derived, not stored:
 * the backend's action_type is platform vocabulary; the prefix is the
 * channel's own.
 */
export function applyToolFor(channel, actionType) {
  if (channel.key === 'facebook') {
    // Meta recs stopped being budget-only when the daily run moved to the
    // full campaign optimizer; the label follows the suggestion.
    const suffix = {
      daily_budget: 'update_budget',
      campaign_daily_budget: 'update_budget',
      status: 'update_status',
      bid_strategy: 'update_bid_strategy',
      creative_status: 'update_creative_status',
      campaign_spend_limit: 'update_spend_limit',
      campaign_dates: 'update_dates',
      geography_targeting: 'update_locations',
      interest_targeting: 'update_interests',
    }[String(actionType || '')] || `update_${String(actionType || 'change')}`;
    return `meta.${suffix}`;
  }
  if (channel.key === 'chatgpt-ads') {
    const suffix = String(actionType || 'edit')
      .replace(/^(geography_targeting|targeting)$/, 'update_locations')
      .replace(/^budget$/, 'update_budget')
      .replace(/^campaign_spend_limit$/, 'update_budget')
      .replace(/^campaign_dates$/, 'update_dates')
      .replace(/^(ad_copy|creative)$/, 'update_creative')
      .replace(/^(bid|bidding)$/, 'update_bid')
      .replace(/^context_hints$/, 'update_context_hints')
      .replace(/^status$/, 'update_status');
    return `chatgptads.${suffix.startsWith('update_') ? suffix : `update_${suffix}`}`;
  }
  if (channel.key !== 'google-ads') return channel.tools.apply;
  const suffix = String(actionType || 'edit')
    .replace(/^negative_keyword_targeting$/, 'update_negatives')
    .replace(/^keyword_targeting$/, 'update_keywords')
    .replace(/^keyword_pause$/, 'pause_keywords')
    .replace(/^geography_targeting$/, 'update_locations')
    .replace(/^campaign_spend_limit$/, 'update_budget')
    .replace(/^budget(_reallocation)?$/, 'update_budget')
    .replace(/^landing_page$/, 'update_landing_page')
    .replace(/^status$/, 'update_status')
    .replace(/^bid$/, 'update_bids');
  return `googleads.${suffix}`;
}


/**
 * Human names for suggestion action types across every platform vocabulary.
 * The card leads with WHAT KIND of change this is; recs stopped being
 * budget-only on either channel, so the category is the reader's first
 * anchor, not a dollar column.
 */
const ACTION_LABELS = {
  daily_budget: 'Budget',
  budget: 'Budget',
  budget_reallocation: 'Budget',
  campaign_spend_limit: 'Spend limit',
  status: 'Status',
  creative_status: 'Creative status',
  bid: 'Bidding',
  bid_strategy: 'Bidding',
  bidding_strategy_target: 'Bidding',
  device_bid_adjustment: 'Bidding',
  keyword_targeting: 'Keywords',
  negative_keyword_targeting: 'Negative keywords',
  keyword_pause: 'Pause keywords',
  ad_copy: 'Ad copy',
  ad_extension: 'Ad extensions',
  campaign_dates: 'Flight dates',
  ad_schedule: 'Ad schedule',
  geography_targeting: 'Geography',
  targeting: 'Geography',
  context_hints: 'Context hints',
  bidding: 'Bidding',
  creative: 'Creative',
  name: 'Name',
  description: 'Description',
  interest_targeting: 'Interests',
  audience_targeting: 'Audiences',
  landing_page: 'Landing page',
};

export function actionLabelFor(actionType) {
  return ACTION_LABELS[String(actionType || '')]
    || String(actionType || 'Change').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
